import { useEffect, useState } from "react";

export type Resource<T> =
  | { status: "loading" }
  | { status: "error"; error: Error }
  /** `refreshing` is set only with `keepPrevious`: `data` is the previous deps' copy while the new one loads. */
  | { status: "ready"; data: T; refreshing?: true };

export interface UseResourceOptions {
  /**
   * When `deps` change, keep showing the last loaded data (marked `refreshing`)
   * instead of dropping back to "loading". Opt-in because the data on screen
   * is then briefly the previous deps': only for readers that can show it as
   * such (dimmed, and never editable) until the new copy arrives.
   */
  keepPrevious?: boolean;
}

/**
 * Minimal data-loading hook: runs `load` whenever `deps` change, tracks
 * loading/error/ready, and ignores results from a superseded call.
 *
 * Also returns `setData`, so a caller that already has a fresher copy of
 * `T` in hand (e.g. a write endpoint's response body) can push it in
 * directly instead of re-running `load` — reads here go through a short
 * CloudFront cache (see `infra/cloudfront.tf`'s `data` cache policy), so
 * re-fetching right after a write can hand back the pre-write copy.
 */
export function useResource<T>(
  load: () => Promise<T>,
  deps: readonly unknown[],
  { keepPrevious = false }: UseResourceOptions = {},
): readonly [Resource<T>, (data: T) => void] {
  const [resource, setResource] = useState<Resource<T>>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setResource((prev) =>
      keepPrevious && prev.status === "ready" ? { status: "ready", data: prev.data, refreshing: true } : { status: "loading" },
    );

    load()
      .then((data) => {
        if (!cancelled) setResource({ status: "ready", data });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setResource({
            status: "error",
            error: err instanceof Error ? err : new Error(String(err)),
          });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  function setData(data: T) {
    setResource({ status: "ready", data });
  }

  return [resource, setData] as const;
}
